// Filterzeile unter dem Listenkopf der Smart Views und der Leads-Liste (Agent 5A, Runde 5). Eigenschaften: `listenVertrag.ts`.
//
// Eingeklappt: „› Filter 8“ mit den ersten Bedingungen als lesbare Pillen („Status: Follow-UP · Bearbeiter: nicht Dilsar Altan …“).
// Ausgeklappt: der Bedingungsbaum zum Bearbeiten (`FilterBaum.tsx`). Ohne Bedingung steht dort „Filter hinzufügen“.
//
// Jede Änderung setzt den Filter-Entwurf der Seite (`onEntwurf`): solange einer gesetzt ist, laden Treffer und Zähler der Seite aus
// `filterApi.treffer` (Abfrage-Engine), nichts ist gespeichert. Ist der Filter wieder gleich dem gespeicherten, geht der Entwurf
// zurück auf `null` (damit „Speichern“ grau wird). Speichern und „Speichern unter“ stehen im Listenkopf (`ListenKopfRechts.tsx`).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Abfrage } from "../../api/filter";
import { kopieren } from "../../werkzeuge/tasten";
import { useMeta } from "../../daten/meta";
import { useSmartViews } from "../../daten/smartviews";
import { Icon } from "../Icon";
import { MenueKnopf, type MenuePunkt } from "../Menue";
import { useMeldung } from "../Meldungen";
import { FilterBaum, FilterKontextProvider, type FilterKontext } from "./FilterBaum";
import { useFilterKatalog } from "./filterDaten";
import {
  blaetter, feldInfo, kanonisch, katalogIndex, leereGruppe, lesen, normalisiert, pillenText, schreiben, standardAbfrage, zaehle,
  type Abfragemodell, type Gruppe, type TextKontext,
} from "./filterModell";
import { FILTER_TEXTE } from "./filterTexte";
import type { FilterZeileProps } from "./listenVertrag";
import "./filter.css";

const SPEICHER_OFFEN = "crm.filter.offen";
const VORSCHAU_MAX = 3;

function offenGelesen(): boolean {
  try {
    return window.localStorage.getItem(SPEICHER_OFFEN) === "1";
  } catch {
    return false;
  }
}
function offenMerken(offen: boolean): void {
  try {
    window.localStorage.setItem(SPEICHER_OFFEN, offen ? "1" : "0");
  } catch {
    /* ohne Speicher bleibt es beim Standard */
  }
}

export function FilterZeile({ art, view, viewId, entwurf, onEntwurf }: FilterZeileProps) {
  const meta = useMeta();
  const { views: alleViews } = useSmartViews();
  const meldung = useMeldung();
  const { katalog, fehler } = useFilterKatalog("lead");
  const index = useMemo(() => katalogIndex(katalog), [katalog]);

  const gespeichert = useMemo<Abfrage | null>(() => (art === "leads" ? (standardAbfrage("lead") as Abfrage) : (view?.s_query ?? null)), [art, view?.s_query]);
  const gespeichertKey = useMemo(() => kanonisch(normalisiert(gespeichert, "lead")), [gespeichert]);
  const gespeichertRef = useRef({ abfrage: gespeichert, key: gespeichertKey });
  gespeichertRef.current = { abfrage: gespeichert, key: gespeichertKey };

  const quelle: Abfrage | null = entwurf?.s_query ?? gespeichert;
  const quelleKey = useMemo(() => kanonisch(normalisiert(quelle, "lead")), [quelle]);

  const [modell, setModell] = useState<Abfragemodell>(() => lesen(quelle, "lead"));
  const modellRef = useRef(modell);
  modellRef.current = modell;
  const emittiert = useRef(quelleKey);
  const [offen, setOffen] = useState(offenGelesen);
  const [offenId, setOffenId] = useState<string | null>(null);
  const [autoWahl, setAutoWahl] = useState(false);
  const autoVerbraucht = useCallback(() => setAutoWahl(false), []);

  // Von außen geändert (andere Smart View geladen, Sortierung der Bedienleiste, Entwurf verworfen): neu lesen. Eigene Änderungen
  // lösen das nicht aus, sonst verlöre der Editor bei jeder Eingabe seine Pillen und das offene Popover.
  useEffect(() => {
    if (quelleKey === emittiert.current) return;
    emittiert.current = quelleKey;
    setModell(lesen(quelle, "lead"));
    setOffenId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quelleKey]);

  const uebernehmen = useCallback(
    (neu: Abfragemodell) => {
      modellRef.current = neu;
      setModell(neu);
      const sq = schreiben(neu);
      const key = kanonisch(normalisiert(sq, "lead"));
      emittiert.current = key;
      if (key === gespeichertRef.current.key) onEntwurf(null);
      else onEntwurf({ s_query: sq as Abfrage });
    },
    [onEntwurf],
  );
  const aendern = useCallback((fn: (w: Gruppe) => Gruppe) => uebernehmen({ ...modellRef.current, wurzel: fn(modellRef.current.wurzel) }), [uebernehmen]);

  const verwerfen = () => {
    const m = lesen(gespeichertRef.current.abfrage, "lead");
    emittiert.current = gespeichertRef.current.key;
    modellRef.current = m;
    setModell(m);
    setOffenId(null);
    onEntwurf(null);
  };

  const text = useMemo<TextKontext>(
    () => ({
      feldName: (ref, ebene) => feldInfo(index, ebene, ref)?.label ?? (ref.type === "custom_field" ? (meta.feld(ref.custom_field_id)?.name ?? ref.custom_field_id) : ref.field_name),
      optionName: (ref, ebene, wert) =>
        feldInfo(index, ebene, ref)?.optionen?.find((o) => o.wert === wert)?.label ?? (wert.startsWith("user_") ? meta.nutzerName(wert) : (meta.status(wert)?.label ?? wert)),
    }),
    [index, meta],
  );

  const kontext = useMemo<FilterKontext>(
    () => ({ katalog, index, text, aendern, offenId, setOffenId, objekt: modell.objekt, nurLesen: !katalog }),
    [katalog, index, text, aendern, offenId, modell.objekt],
  );

  const umschalten = () => {
    const neu = !offen;
    setOffen(neu);
    offenMerken(neu);
    if (!neu) setOffenId(null);
  };

  const anzahl = zaehle(modell.wurzel);
  const vorschau = blaetter(modell.wurzel).filter((k) => k.typ === "feld" || k.typ === "volltext");
  const weitere = anzahl - Math.min(vorschau.length, VORSCHAU_MAX);

  const jsonKopieren = () =>
    void kopieren(JSON.stringify(schreiben(modell), null, 2)).then((ok) => meldung.zeige(ok ? "ok" : "fehler", ok ? FILTER_TEXTE.json.kopiert : FILTER_TEXTE.json.kopierenFehler));

  const jsonEinfuegen = async () => {
    try {
      const roh = JSON.parse(await navigator.clipboard.readText()) as unknown;
      const sq = roh && typeof roh === "object" && "query" in roh ? (roh as Abfrage) : ({ query: roh } as Abfrage);
      const m = lesen(sq, "lead");
      if (m.objekt !== "lead" || (!Array.isArray((sq.query as { queries?: unknown })?.queries) && !(sq.query as { type?: unknown })?.type)) throw new Error("kein Lead-Filter");
      uebernehmen({ ...m, sort: Array.isArray(sq.sort) && sq.sort.length ? m.sort : modellRef.current.sort });
      setOffen(true);
      meldung.zeige("ok", FILTER_TEXTE.json.eingefuegt);
    } catch {
      meldung.zeige("fehler", FILTER_TEXTE.json.einfuegenFehler);
    }
  };

  const menue: MenuePunkt[] = [
    { label: FILTER_TEXTE.json.kopieren, icon: "kopieren", onClick: jsonKopieren },
    { label: FILTER_TEXTE.json.einfuegen, icon: "filter", onClick: () => void jsonEinfuegen(), deaktiviert: !katalog },
    { label: FILTER_TEXTE.json.alleEntfernen, icon: "schliessen", onClick: () => uebernehmen({ ...modell, wurzel: leereGruppe(), flach: true }), deaktiviert: anzahl === 0 || !katalog, trenner: true },
  ];

  // Kontakt- und Opportunity-Smart-Views zeigt die Oberfläche (noch) nicht als Liste; für sie gibt es keine Filterzeile
  // (die Art steht auch in der Liste der Smart Views, falls die Definition wegen eines Fehlers nicht geladen wurde)
  const typ = view?.type ?? (viewId ? alleViews?.find((v) => v.id === viewId)?.type : undefined) ?? "lead";
  if (art === "smartview" && (typ || "lead") !== "lead") return null;

  return (
    <div className={`fz${entwurf ? " fz--entwurf" : ""}`}>
      <div className="fz__zeile">
        {anzahl > 0 || offen ? (
          <button type="button" className="fz__toggle" aria-expanded={offen} title={offen ? FILTER_TEXTE.einklappen : FILTER_TEXTE.ausklappen} onClick={umschalten}>
            <Icon name={offen ? "chevron-unten" : "chevron-rechts"} groesse={12} />
            {FILTER_TEXTE.filter}
            {anzahl > 0 ? <span className="fz__zahl">{anzahl}</span> : null}
          </button>
        ) : (
          <button
            type="button"
            className="fz__toggle fz__toggle--neu"
            disabled={!katalog}
            title={fehler ? FILTER_TEXTE.katalogFehlt : undefined}
            onClick={() => {
              setOffen(true);
              offenMerken(true);
              setAutoWahl(true);
            }}
          >
            <Icon name="plus" groesse={13} />
            {FILTER_TEXTE.filterHinzufuegen}
          </button>
        )}
        {!offen && anzahl > 0 ? (
          <span className="fz__vorschau" aria-label="Bedingungen">
            {vorschau.slice(0, VORSCHAU_MAX).map((k) => {
              const t = pillenText(k as Parameters<typeof pillenText>[0], modell.objekt, text);
              return (
                <button key={k.id} type="button" className="fz__mini" onClick={umschalten} title={`${t.feld}: ${t.op} ${t.wert}`.replace(/\s+/g, " ")}>
                  <span className="fz__minifeld">{t.feld}:</span> {t.op ? <span className="fz__miniop">{t.op}</span> : null} <b>{t.wert}</b>
                </button>
              );
            })}
            {weitere > 0 ? (
              <button type="button" className="fz__mehr" onClick={umschalten}>
                {`+${weitere}`}
              </button>
            ) : null}
          </span>
        ) : null}
        <span className="fz__rechts">
          {entwurf ? (
            <>
              <span className="fz__entwurftext">{FILTER_TEXTE.nichtGespeichert}</span>
              <button type="button" className="textknopf" title={FILTER_TEXTE.verwerfenTitel} onClick={verwerfen}>
                {FILTER_TEXTE.verwerfen}
              </button>
            </>
          ) : null}
          <MenueKnopf knopfKlasse="symbolknopf symbolknopf--klein" titel="Mehr zum Filter" punkte={menue} ausrichtung="end" breite={300}>
            <Icon name="mehr" groesse={15} />
          </MenueKnopf>
        </span>
      </div>
      {offen ? (
        <div className="fz__panel">
          {fehler && !katalog ? <p className="fz__fehler">{FILTER_TEXTE.katalogFehlt}</p> : null}
          <FilterKontextProvider value={kontext}>
            <FilterBaum wurzel={modell.wurzel} objekt={modell.objekt} automatisch={autoWahl} onAutoVerbraucht={autoVerbraucht} />
          </FilterKontextProvider>
        </div>
      ) : null}
    </div>
  );
}
